/**
 * Enregistreur de partie (télémétrie v2) : une session couvre toute la partie, de l'entrée dans le monde à la
 * fermeture. Il reçoit les événements de la simulation, les entrées de chaque tick, les actions de menu, les
 * erreurs et les signalements, et produit un enregistrement { header, summary, log } envoyé par points de
 * contrôle. Aucun accès au navigateur ici : le contexte machine est fourni par l'appelant.
 * Définitions des champs : tools/telemetry/METRICS.md.
 */
import type { ControlMode } from '../sim/entities';
import type { SimEvent } from '../sim/events';
import type { PlayerInput } from '../sim/pilots/player';
import type { SessionSummary as MissionSummary, TelemetryLogEntry } from '../sim/telemetry/telemetry';

export const SESSION_SCHEMA = 'space-arena-web/2';
/** Cadence des échantillons de vol (secondes de simulation). */
export const SAMPLE_INTERVAL = 1;
/** Au-delà, le journal n'accepte plus que les erreurs et signalements (le compteur `droppedLogEntries` le dit). */
export const LOG_LIMIT = 120_000;

/** Événements trop fréquents pour le journal (ils restent comptés). */
const NOT_LOGGED = new Set(['laserFired', 'projectileSpawned', 'projectileRemoved', 'impact', 'explosion', 'lockChanged', 'missileBlocked']);

export interface MachineInfo {
  userAgent: string;
  platform: string;
  /** Marques et plateforme rapportées par le navigateur (User-Agent Client Hints), si disponibles. */
  brands: string;
  mobile: boolean;
  language: string;
  timezone: string;
  cpuCores: number | null;
  memoryGb: number | null;
  screen: { width: number; height: number; colorDepth: number };
  devicePixelRatio: number;
  viewport: { width: number; height: number };
  touchPoints: number;
  gpu: { vendor: string; renderer: string };
  webgl: { version: number; maxTextureSize: number };
  engine: string;
}

export interface GameSessionHeader {
  schema: typeof SESSION_SCHEMA;
  sessionId: string;
  startedAt: string;
  updatedAt: string;
  player: string;
  version: string;
  buildCommit: string;
  buildDirty: boolean;
  buildDate: string;
  dataHash: string;
  seed: number;
  /** Toujours « world » (les sessions v1 portaient le mode de mission). */
  mode: 'world';
  startDock: string;
  /** Appareil au départ (les changements sont dans le journal, événements `refit`). */
  ship: string;
  mouseMode: string;
  device: string;
  scripted: boolean;
  autopilot: boolean;
  settings: Record<string, unknown>;
  machine: MachineInfo;
  resolution: string;
  fps: { average: number; p05: number; samples: number };
  realSeconds: number;
}

export interface MissionEntry {
  scenario: string;
  ship: string;
  loadout: Record<string, string> | null;
  /** Temps du monde (s) au début et à la fin. */
  startT: number;
  endT: number;
  outcome: string;
  credits: number;
  summary: MissionSummary;
}

export interface ControlStats {
  deviceSeconds: { keyboard: number; gamepad: number };
  thrustSeconds: number;
  strafeSeconds: number;
  verticalSeconds: number;
  rollSeconds: number;
  boostSeconds: number;
  brakeSeconds: number;
  fireSeconds: number;
  secondaryPresses: number;
  mouseTravelDeg: number;
  stickLookSeconds: number;
  throttleChanges: number;
  skipIntermissions: number;
}

export interface GameSessionSummary {
  /** `ongoing` (point de contrôle), `quit` (retour au titre), `closed` (onglet fermé). */
  outcome: 'ongoing' | 'quit' | 'closed';
  simSeconds: number;
  realSeconds: number;
  /** Temps de simulation par état du joueur, plus le temps réel passé en pause. */
  time: { flight: number; docking: number; docked: number; launching: number; destroyed: number; paused: number };
  missions: MissionEntry[];
  missionOutcomes: Record<string, number>;
  kills: Record<string, number>;
  deaths: number;
  deathsBySource: Record<string, number>;
  damageDealt: Record<string, number>;
  damageReceived: Record<string, number>;
  shieldAbsorbed: number;
  laserShots: number;
  laserHits: number;
  missilesLaunched: number;
  swarmLaunched: number;
  docks: Record<string, number>;
  launches: number;
  pickups: number;
  overheats: number;
  boostDepletions: number;
  shieldBreaks: number;
  emergencyRepairs: number;
  bestMultiplier: number;
  credits: { earned: number; spent: number; balance: number };
  purchases: number;
  equips: number;
  refits: number;
  settingsChanges: number;
  controls: ControlStats;
  errors: number;
  bugReports: number;
  droppedLogEntries: number;
}

/** Instantané pour les échantillons de vol. */
export interface SampleState {
  position: [number, number, number];
  speed: number;
  health: number;
  maxHealth: number;
  shield: number;
  throttle: number;
  mode: ControlMode;
  enemies: number;
  mission: string;
  fps: number;
}

const r1 = (v: number) => Math.round(v * 10) / 10;
const r3 = (v: number) => Math.round(v * 1000) / 1000;
/** Valeurs d'événement allégées pour le journal : nombres à 3 décimales, vecteurs à 2. */
const compactValue = (v: unknown): unknown =>
  typeof v === 'number' ? r3(v) : Array.isArray(v) ? v.map((x) => (typeof x === 'number' ? Math.round(x * 100) / 100 : compactValue(x))) : v;

function emptySummary(): GameSessionSummary {
  return {
    outcome: 'ongoing',
    simSeconds: 0,
    realSeconds: 0,
    time: { flight: 0, docking: 0, docked: 0, launching: 0, destroyed: 0, paused: 0 },
    missions: [],
    missionOutcomes: {},
    kills: {},
    deaths: 0,
    deathsBySource: {},
    damageDealt: {},
    damageReceived: {},
    shieldAbsorbed: 0,
    laserShots: 0,
    laserHits: 0,
    missilesLaunched: 0,
    swarmLaunched: 0,
    docks: {},
    launches: 0,
    pickups: 0,
    overheats: 0,
    boostDepletions: 0,
    shieldBreaks: 0,
    emergencyRepairs: 0,
    bestMultiplier: 1,
    credits: { earned: 0, spent: 0, balance: 0 },
    purchases: 0,
    equips: 0,
    refits: 0,
    settingsChanges: 0,
    controls: {
      deviceSeconds: { keyboard: 0, gamepad: 0 },
      thrustSeconds: 0,
      strafeSeconds: 0,
      verticalSeconds: 0,
      rollSeconds: 0,
      boostSeconds: 0,
      brakeSeconds: 0,
      fireSeconds: 0,
      secondaryPresses: 0,
      mouseTravelDeg: 0,
      stickLookSeconds: 0,
      throttleChanges: 0,
      skipIntermissions: 0,
    },
    errors: 0,
    bugReports: 0,
    droppedLogEntries: 0,
  };
}

const add = (map: Record<string, number>, key: string, value = 1) => { map[key] = (map[key] ?? 0) + value; };

export class SessionRecorder {
  readonly log: TelemetryLogEntry[] = [];
  private readonly s = emptySummary();
  private lastPlayerDamageSource = 'unknown';
  private nextSample = 0;
  private secondaryWasDown = false;
  private now = 0;

  constructor(readonly header: GameSessionHeader) {}

  get summary(): Readonly<GameSessionSummary> { return this.s; }

  private push(entry: TelemetryLogEntry, essential = false): void {
    if (this.log.length >= LOG_LIMIT && !essential) {
      this.s.droppedLogEntries++;
      return;
    }
    this.log.push(entry);
  }

  /** Un tick de simulation : temps par état et statistiques de contrôles (en vol seulement). */
  onTick(t: number, dt: number, mode: ControlMode, input: PlayerInput, device: 'keyboard' | 'gamepad'): void {
    this.now = t;
    // Le monde démarre à t = 0 avec la session.
    this.s.simSeconds = r3(t);
    this.s.time[mode] += dt;
    if (mode !== 'flight') {
      this.secondaryWasDown = input.fireSecondary;
      return;
    }
    const c = this.s.controls;
    c.deviceSeconds[device] += dt;
    if (Math.abs(input.thrust.z) > 0.1 || Math.abs(input.throttleAxis) > 0.1) c.thrustSeconds += dt;
    if (Math.abs(input.thrust.x) > 0.1) c.strafeSeconds += dt;
    if (Math.abs(input.thrust.y) > 0.1) c.verticalSeconds += dt;
    if (Math.abs(input.roll) > 0.1) c.rollSeconds += dt;
    if (input.boost) c.boostSeconds += dt;
    if (input.brake) c.brakeSeconds += dt;
    if (input.firePrimary) c.fireSeconds += dt;
    if (input.fireSecondary && !this.secondaryWasDown) c.secondaryPresses++;
    this.secondaryWasDown = input.fireSecondary;
    c.mouseTravelDeg += Math.abs(input.mouseYawDeg) + Math.abs(input.mousePitchDeg);
    if (Math.abs(input.lookYaw) + Math.abs(input.lookPitch) > 0.15) c.stickLookSeconds += dt;
    if (input.throttleStep !== 0 || input.throttleZero) c.throttleChanges++;
    if (input.skipIntermission) c.skipIntermissions++;
  }

  /** Échantillon de vol, à la cadence `SAMPLE_INTERVAL` (l'appelant le propose à chaque tick). */
  sample(t: number, state: () => SampleState): void {
    if (t < this.nextSample) return;
    this.nextSample = t + SAMPLE_INTERVAL;
    const s = state();
    this.push({
      t: r3(t),
      type: 'sample',
      p: s.position.map(r1),
      v: r1(s.speed),
      hp: r1(s.health),
      hpMax: s.maxHealth,
      sh: r1(s.shield),
      thr: Math.round(s.throttle * 100) / 100,
      mode: s.mode,
      en: s.enemies,
      ms: s.mission,
      fps: Math.round(s.fps),
    });
  }

  /** Événements de la simulation (temps du monde). */
  onEvents(events: SimEvent[]): void {
    const s = this.s;
    for (const e of events) {
      if (!NOT_LOGGED.has(e.type)) {
        const { type, time, ...rest } = e;
        const entry: TelemetryLogEntry = { t: r3(time), type };
        for (const [key, value] of Object.entries(rest)) entry[key] = compactValue(value);
        this.push(entry);
      }
      switch (e.type) {
        case 'laserFired': s.laserShots++; break;
        case 'damage':
          if (e.source === 'player' && e.targetKind !== 'player') {
            add(s.damageDealt, e.weapon, e.applied);
            if (e.weapon === 'laser' && e.applied + e.absorbed > 0) s.laserHits++;
            if (e.killed) add(s.kills, e.targetKind);
          } else if (e.targetKind === 'player') {
            add(s.damageReceived, e.source, e.applied);
            s.shieldAbsorbed += e.absorbed;
            if (e.applied + e.absorbed > 0) this.lastPlayerDamageSource = e.source;
          }
          break;
        case 'playerDown':
          s.deaths++;
          add(s.deathsBySource, this.lastPlayerDamageSource);
          break;
        case 'missileLaunched':
          if (e.kind === 'missile') s.missilesLaunched += e.count;
          else s.swarmLaunched += e.count;
          break;
        case 'dock':
          if (e.phase === 'docked') add(s.docks, e.dock);
          if (e.phase === 'launched') s.launches++;
          break;
        case 'pickupCollected': s.pickups++; break;
        case 'overheat': s.overheats++; break;
        case 'boostDepleted': s.boostDepletions++; break;
        case 'shieldBroken': s.shieldBreaks++; break;
        case 'emergencyRepair': s.emergencyRepairs++; break;
        case 'score': s.bestMultiplier = Math.max(s.bestMultiplier, e.multiplier); break;
        default: break;
      }
    }
  }

  /** Action hors simulation : menus, achats, réglages, pauses… */
  appEvent(type: string, data: Record<string, unknown> = {}): void {
    const s = this.s;
    if (type === 'purchase') { s.purchases++; s.credits.spent += Number(data.price ?? 0); }
    else if (type === 'equip') s.equips++;
    else if (type === 'refit') s.refits++;
    else if (type === 'setting') s.settingsChanges++;
    this.push({ t: r3(this.now), type, ...data });
  }

  /** Erreur JavaScript ou console, pour les rapports de bug. */
  error(message: string, detail = ''): void {
    this.s.errors++;
    if (this.s.errors > 200) return;
    this.push({ t: r3(this.now), type: 'error', message: message.slice(0, 500), detail: detail.slice(0, 2000) }, true);
  }

  /** Signalement d'un testeur, avec l'état du jeu au moment du signalement. */
  bugReport(text: string, context: Record<string, unknown>): void {
    this.s.bugReports++;
    this.push({ t: r3(this.now), type: 'bugReport', text: text.slice(0, 4000), ...context }, true);
  }

  addMission(entry: MissionEntry): void {
    this.s.missions.push(entry);
    add(this.s.missionOutcomes, entry.outcome);
    this.s.credits.earned += entry.credits;
  }

  addPausedSeconds(dt: number): void {
    this.s.time.paused += dt;
  }

  /** Enregistrement complet à l'instant présent (copie : l'enregistreur continue). */
  toRecord(outcome: GameSessionSummary['outcome'], context: { realSeconds: number; fps: GameSessionHeader['fps']; device: string; balance: number }) {
    const summary = structuredClone(this.s);
    summary.outcome = outcome;
    summary.realSeconds = r1(context.realSeconds);
    summary.credits.balance = context.balance;
    for (const key of Object.keys(summary.time) as (keyof GameSessionSummary['time'])[]) summary.time[key] = r3(summary.time[key]);
    for (const key of Object.keys(summary.controls) as (keyof ControlStats)[]) {
      const v = summary.controls[key];
      if (typeof v === 'number') (summary.controls as unknown as Record<string, number>)[key] = r3(v);
    }
    summary.controls.deviceSeconds = { keyboard: r3(summary.controls.deviceSeconds.keyboard), gamepad: r3(summary.controls.deviceSeconds.gamepad) };
    summary.shieldAbsorbed = r1(summary.shieldAbsorbed);
    const header: GameSessionHeader = { ...this.header, updatedAt: new Date().toISOString(), realSeconds: summary.realSeconds, fps: context.fps, device: context.device };
    return { header, summary, log: this.log.slice() };
  }
}
